(() => {
  if (customElements.get('mz-logo-showcase')) return;

  class MzLogoShowcase extends HTMLElement {
    constructor() {
      super();
      this.motionButton = null;
      this.motionLabel = null;
      this.track = null;
      this.viewport = null;
      this.groups = [];
      this.labels = { pause: 'Logo-Animation pausieren', play: 'Logo-Animation fortsetzen' };
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.outsidePaused = false;
      this.hiddenPaused = document.hidden;
      this.reducedMotion = false;
      this.motionOverride = false;
      this.ended = false;
      this.intersectionObserver = null;
      this.resizeObserver = null;
      this.resizeFrame = null;
      this.reducedMotionQuery = null;
      this.focusTimer = null;

      this.onMotionClick = this.onMotionClick.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onAnimationEnd = this.onAnimationEnd.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-logo-track]');
      this.viewport = this.querySelector('[data-logo-viewport]');
      this.groups = Array.from(this.querySelectorAll('[data-logo-group]'));
      this.motionButton = this.querySelector('[data-logo-motion]');
      this.motionLabel = this.querySelector('[data-logo-motion-label]');

      const labelsNode = this.querySelector('[data-logo-labels]');
      if (labelsNode) {
        try {
          this.labels = { ...this.labels, ...JSON.parse(labelsNode.textContent) };
        } catch (_error) {
          // Keep the safe defaults when theme-editor JSON is incomplete.
        }
      }

      if (
        this.dataset.layout !== 'slider' ||
        this.dataset.autoplay !== 'true' ||
        !this.track ||
        !this.viewport ||
        this.groups.length < 2
      ) {
        if (this.motionButton) this.motionButton.hidden = true;
        this.classList.add('is-ready');
        return;
      }

      this.fillGroups();

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.reducedMotionQuery.matches;

      this.motionButton?.addEventListener('click', this.onMotionClick);
      this.track.addEventListener('animationend', this.onAnimationEnd);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:section:select', this.onEditorSelect);

      if (this.dataset.pauseHover === 'true') {
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
      }

      if ('addEventListener' in this.reducedMotionQuery) {
        this.reducedMotionQuery.addEventListener('change', this.onReducedMotionChange);
      } else {
        this.reducedMotionQuery.addListener(this.onReducedMotionChange);
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.outsidePaused = !entries[0]?.isIntersecting;
            this.updateMotion();
          },
          { threshold: 0.1 }
        );
        this.intersectionObserver.observe(this);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize);
      }

      if (window.Shopify?.designMode) this.userPaused = true;

      requestAnimationFrame(() => {
        this.classList.add('is-ready');
        this.updateMotion();
      });
    }

    disconnectedCallback() {
      this.motionButton?.removeEventListener('click', this.onMotionClick);
      this.track?.removeEventListener('animationend', this.onAnimationEnd);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);

      if (this.reducedMotionQuery) {
        if ('removeEventListener' in this.reducedMotionQuery) {
          this.reducedMotionQuery.removeEventListener('change', this.onReducedMotionChange);
        } else {
          this.reducedMotionQuery.removeListener(this.onReducedMotionChange);
        }
      }

      if (this.focusTimer) window.clearTimeout(this.focusTimer);
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
    }

    onMotionClick() {
      const isCurrentlyPaused = this.classList.contains('is-paused');

      if (isCurrentlyPaused) {
        this.userPaused = false;
        this.hoverPaused = false;
        this.focusPaused = false;
        if (this.reducedMotion) this.motionOverride = true;
      } else {
        this.userPaused = true;
      }

      if (this.ended && !this.userPaused) this.restartAnimation();
      this.updateMotion();
    }

    onPointerEnter() {
      this.hoverPaused = true;
      this.updateMotion();
    }

    onPointerLeave() {
      this.hoverPaused = false;
      this.updateMotion();
    }

    onFocusIn(event) {
      if (this.focusTimer) window.clearTimeout(this.focusTimer);
      if (event.target === this.motionButton) return;
      this.focusPaused = true;
      this.updateMotion();
    }

    onFocusOut() {
      if (this.focusTimer) window.clearTimeout(this.focusTimer);
      this.focusTimer = window.setTimeout(() => {
        this.focusPaused = this.contains(document.activeElement);
        this.updateMotion();
      }, 0);
    }

    onVisibilityChange() {
      this.hiddenPaused = document.hidden;
      this.updateMotion();
    }

    onReducedMotionChange(event) {
      this.reducedMotion = event.matches;
      if (!this.reducedMotion) this.motionOverride = false;
      this.updateMotion();
    }

    onAnimationEnd() {
      if (this.dataset.loop === 'true') return;
      this.ended = true;
      this.classList.remove('is-running');
      this.classList.add('is-paused', 'is-ended');
      if (this.motionButton) this.motionButton.hidden = true;
    }

    onEditorSelect(event) {
      if (event.target?.contains(this)) {
        this.userPaused = true;
        this.updateMotion();
      }
    }

    onResize() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.fillGroups();
        if (!this.ended) this.restartAnimation();
        this.updateMotion();
      });
    }

    makeVisualClone(item) {
      const clone = item.cloneNode(true);
      clone.dataset.logoFillClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.classList.remove('shopify-block');

      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    }

    fillGroups() {
      if (!this.viewport || this.groups.length < 2) return;

      this.groups.forEach((group) => {
        Array.from(group.children).forEach((item) => {
          if (item.hasAttribute('data-logo-fill-clone')) item.remove();
        });
      });

      const gap = Number.parseFloat(window.getComputedStyle(this.groups[0]).columnGap) || 0;
      const targetWidth = this.viewport.clientWidth + gap;

      this.groups.forEach((group) => {
        const originals = Array.from(group.children);
        if (!originals.length) return;

        let cloneCount = 0;
        while (group.scrollWidth < targetWidth && cloneCount < 100) {
          const source = originals[cloneCount % originals.length];
          group.appendChild(this.makeVisualClone(source));
          cloneCount += 1;
        }
      });
    }

    restartAnimation() {
      if (!this.track) return;
      this.ended = false;
      this.classList.remove('is-ended');
      if (this.motionButton) this.motionButton.hidden = false;
      this.track.style.animationName = 'none';
      void this.track.offsetWidth;
      this.track.style.animationName = '';
    }

    updateMotion() {
      const reducePaused = this.reducedMotion && !this.motionOverride;
      const paused =
        this.userPaused ||
        this.hoverPaused ||
        this.focusPaused ||
        this.outsidePaused ||
        this.hiddenPaused ||
        reducePaused ||
        this.ended;

      this.classList.toggle('is-running', !paused);
      this.classList.toggle('is-paused', paused);

      if (!this.motionButton) return;
      const label = paused ? this.labels.play : this.labels.pause;
      this.motionButton.setAttribute('aria-pressed', String(paused));
      this.motionButton.setAttribute('aria-label', label);
      if (this.motionLabel) this.motionLabel.textContent = label;
    }
  }

  customElements.define('mz-logo-showcase', MzLogoShowcase);
})();
